import { useMemo, useState } from 'react';
import { Bell } from 'lucide-react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EmptyState, Popover } from '../../components';
import { cx } from '../../lib/cx';
import { useRequiredWorkspace } from '../workspace/useWorkspace';
import { fetchNotifications, fetchUnreadCount, markNotificationsRead } from './api';
import { notificationQueryKeys } from './queryKeys';
import { formatNotification } from './formatNotification';
import { formatRelativeTime } from './relativeTime';
import { useNotificationsRealtimeRefresh } from './useNotificationsRealtimeRefresh';
import type { NotificationRow } from './types';

/**
 * The bell: "what happened since I was last here?"
 *
 * Self-contained per the brief — AppShell.tsx (the lead's file, already
 * carrying a `TODO(notifications)` marker where this mounts) needs nothing
 * beyond `<NotificationBell />`. It reads the current workspace off
 * `useRequiredWorkspace`, exactly like every other header control.
 *
 * The server has already applied the visibility rule to everything this
 * fetches — see notifications.service.ts's file header — so nothing here
 * re-filters a response that already arrived, same posture as every other
 * feature in this codebase that reads server state.
 */

const LIST_LIMIT = 50;

interface RequestGroup {
  readonly requestId: string;
  readonly requestNumber: number | null;
  readonly requestTitle: string;
  readonly items: readonly NotificationRow[];
}

/**
 * `items` arrives newest-first from the API. Grouping by walking it in that
 * order — rather than sorting groups afterwards by some derived "latest"
 * timestamp — is what keeps the GROUPS newest-first too: the first time a
 * request is seen while walking the list is necessarily its most recent
 * event, so the group belongs wherever that first sighting was.
 */
function groupByRequest(items: readonly NotificationRow[]): RequestGroup[] {
  const order: string[] = [];
  const byRequest = new Map<string, NotificationRow[]>();

  for (const item of items) {
    const existing = byRequest.get(item.requestId);
    if (existing) {
      existing.push(item);
    } else {
      byRequest.set(item.requestId, [item]);
      order.push(item.requestId);
    }
  }

  return order.map((requestId) => {
    const groupItems = byRequest.get(requestId)!;
    return {
      requestId,
      requestNumber: groupItems[0]!.requestNumber,
      requestTitle: groupItems[0]!.requestTitle,
      items: groupItems,
    };
  });
}

export function NotificationBell() {
  const { workspace } = useRequiredWorkspace();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  // Keeps the badge (and, while open, the panel) live on the same realtime
  // events every other screen reacts to — see that hook's header for why this
  // does not mean opening a second socket.
  useNotificationsRealtimeRefresh(workspace.id);

  // The badge stays mounted and querying even while the panel is closed —
  // that is the entire point of a badge. The list only fetches once opened,
  // so a person who never opens the bell never pays for it.
  const countQuery = useQuery({
    queryKey: notificationQueryKeys.count(workspace.id),
    queryFn: ({ signal }) => fetchUnreadCount(workspace.id, signal),
  });

  const listQuery = useQuery({
    queryKey: notificationQueryKeys.list(workspace.id, false),
    queryFn: ({ signal }) => fetchNotifications(workspace.id, { limit: LIST_LIMIT }, signal),
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: (ids?: readonly string[]) => markNotificationsRead(workspace.id, ids),
    onSuccess: () => {
      // One prefix invalidates both the count and the list — see
      // queryKeys.ts's header for why `all()` is a true prefix of both.
      void queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all(workspace.id) });
    },
  });

  const unread = countQuery.data?.unread ?? 0;
  const items = listQuery.data?.items ?? [];
  const groups = useMemo(() => groupByRequest(items), [items]);

  const handleEntryClick = (row: NotificationRow) => {
    setOpen(false);
    // Fire-and-forget: navigation must not wait on this. `Link` already
    // starts the route change; marking read is a side effect layered on top.
    if (row.readAt === null) markRead.mutate([row.id]);
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      label="Notifications"
      className="w-[23.75rem] max-w-[calc(100vw-2rem)]"
      trigger={
        <button
          type="button"
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
          className="relative grid size-8 flex-none place-items-center rounded-md border border-transparent text-muted hover:bg-surface-2 hover:text-ink"
        >
          <Bell className="size-4" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 grid h-4 min-w-4 place-items-center rounded-full bg-accent px-1 fs-10 leading-none font-bold text-white"
              aria-hidden="true"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      }
    >
      {/* The panel. Radix owns opening, closing, focus and the ARIA on the
          trigger; this is only what goes inside. The click-catcher this used
          to rely on — an invisible full-screen <button> that announced itself
          as "Close notifications, button" over the whole viewport — is gone,
          and Escape now works, which it did not. */}
      <>
        <div>
          <div className="flex items-center justify-between border-b border-line px-3.5 py-2.5">
            <h2 className="fs-13 font-semibold text-ink">Notifications</h2>
            <button
              type="button"
              disabled={unread === 0 || markRead.isPending}
              onClick={() => markRead.mutate(undefined)}
              className="fs-12 font-medium text-accent disabled:cursor-not-allowed disabled:text-faint"
            >
              Mark all read
            </button>
          </div>

          <div className="max-h-[26.25rem] overflow-y-auto">
            {listQuery.isPending ? (
              <p className="px-4 py-8 text-center fs-12 text-muted">Loading…</p>
            ) : listQuery.error !== null ? (
              <p className="px-4 py-8 text-center fs-12 text-danger">{listQuery.error.message}</p>
            ) : groups.length === 0 ? (
              // Reassurance, not emptiness, per the brief: this is the
              // steady state of someone on top of their inbox, not a
              // feature nobody has used yet.
              <EmptyState
                title="You're all caught up"
                description="Nothing new since you were last here. Replies, mentions and status changes will show up in this list."
              />
            ) : (
              <ul>
                {groups.map((group) => (
                  <li key={group.requestId} className="border-b border-surface-3 last:border-b-0">
                    <div className="px-3.5 pt-2.5 pb-1 fs-11 font-semibold text-muted">
                      {group.requestNumber !== null && (
                        <>
                          <span className="numeric">#{group.requestNumber}</span> ·{' '}
                        </>
                      )}
                      {group.requestTitle}
                    </div>
                    <ul>
                      {group.items.map((row) => (
                        <li key={row.id}>
                          <Link
                            // The thread view, which opens a chat and an
                            // issue alike — /requests/:id cannot show a chat.
                            to={`/t/${row.requestId}`}
                            onClick={() => handleEntryClick(row)}
                            className="flex items-start gap-2 px-3.5 py-2 fs-12.5 hover:bg-surface-2"
                          >
                            <span
                              className={cx(
                                'mt-1.5 size-1.5 flex-none rounded-full',
                                row.readAt === null ? 'bg-accent' : 'bg-transparent',
                              )}
                              aria-hidden="true"
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block text-ink-soft">{formatNotification(row)}</span>
                              <span className="fs-11 text-faint">{formatRelativeTime(row.createdAt)}</span>
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </>
    </Popover>
  );
}
